import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Badge from 'Components/DesignSystem/Badge/Badge';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { useISRContext } from '../Hooks/useISRContext';
import { AppContext } from "../../../../Context/AppContext";

type TFieldProps = {
    onClose: () => void;
    onFieldSubmit: (categories: []) => void;
}

/**
 * The list-level "Select Categories to Promote" dialog on the design system
 * (§ 89): DS Dialog shell, DS Checkbox — the legacy checked state was an
 * `<img>` fetched from cdn.builder.io, so a CDN blip rendered every selected
 * row as unselected — DS Badge for the count, DS Spinner, DS Tooltip for the
 * model list. Selection/submit logic is unchanged: Promote stays ENABLED with
 * nothing selected and toasts "Please select category to continue" — that is
 * the shipped contract (okf/isr/e2e.md § 4), do not swap it for a disabled
 * button.
 */
const PromotionDialog = ({ onClose, onFieldSubmit }: TFieldProps) => {
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [cats, setCats] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const isrContext = useISRContext();
    const appData = useContext(AppContext);

    useEffect(() => {
        const fetchIndustryCategories = async () => {
            if (!isrContext?.industryId) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const responseData = await isrContext?.getIndustryChannelCategories(isrContext?.industryId, appData.channelKey);
                if (responseData.done === true) {
                    setCats((responseData.data ?? []).map((e: any) => {
                        return { ...e, selected: false };
                    }));
                } else {
                    setCats([]);
                }
            } catch (e) {
                setCats([]);
                toast.error('Failed to load categories');
            } finally {
                setLoading(false);
            }
        };

        fetchIndustryCategories();
    }, []);

    const handleSelection = (index: number) => {
        console.log(index);
        const updatedItems = cats.map((e: any, i: number) => i === index ? { ...e, selected: !e.selected } : e)
        setCats(updatedItems);
    };

    const handleSelectAll = () => {
        const updatedItems = cats.map((e: any) => {
            return { ...e, selected: !selectAll };
        });
        setCats(updatedItems);
        setSelectAll(!selectAll);
    };

    const selectedCount = cats.filter((e: any) => e.selected).length;

    const footer = (
        <div className='tw-flex tw-w-full tw-gap-2 tw-justify-end'>
            <Button
                variant="secondary"
                data-testid="components-promotiondialog-button-1"
                id="promotion-dialog-cancel-btn"
                onClick={onClose}
                disabled={loading}
            >
                Cancel
            </Button>
            <Button
                variant="primary"
                data-testid="components-promotiondialog-button-2"
                disabled={loading}
                onClick={() => {
                    if (selectedCount > 0) {
                        onFieldSubmit(cats.filter((e: any) => e.selected).map((item: any) => item.categoryId));
                    } else {
                        toast.error('Please select category to continue');
                    }
                }}
            >
                Promote
            </Button>
        </div>
    );

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Select Categories to Promote"
            data-testid="promotion-dialog"
            footer={footer}
            headerActions={!loading ? (
                <Badge data-testid="promotion-dialog-selected-count">{selectedCount} Selected</Badge>
            ) : undefined}
        >
            {loading ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                    <Spinner size="md" data-testid="promotion-dialog-loading" />
                </div>
            ) : (
                <>
                    {/* Column header row, with the select-all checkbox. The row itself
                        stays click-to-toggle (its legacy contract); the checkbox stops
                        propagation so its own toggle doesn't fire the row's a second
                        time. */}
                    <div
                        role="button"
                        data-testid="components-promotiondialog-div-1"
                        className="tw-flex tw-items-center tw-gap-4 tw-px-3.5 tw-py-3 tw-w-full tw-bg-surface-sunken tw-border tw-border-edge tw-rounded-t-ctl tw-cursor-pointer"
                        onClick={() => handleSelectAll()}
                    >
                        <span onClick={(e) => e.stopPropagation()} className="tw-flex tw-items-center">
                            <Checkbox
                                checked={selectAll}
                                onChange={() => handleSelectAll()}
                                aria-label="Select all categories"
                                data-testid="promotion-dialog-select-all"
                            />
                        </span>
                        <Typography variant="sub-title-label" as="span" className="tw-w-[60%] tw-text-ink-secondary">Category</Typography>
                        <Typography variant="sub-title-label" as="span" className="tw-w-[30%] tw-text-ink-secondary">Number of Models</Typography>
                    </div>
                    {cats.length <= 0 ? (
                        <Typography variant="body-primary" as="div" className="tw-py-3">No categories found</Typography>
                    ) : null}
                    <div className={`tw-max-h-[400px] tw-overflow-y-auto`}>
                        {cats.map((e: any, index: number) => {
                            return <div role="button" data-testid={`components-promotiondialog-div-2-${index}`}
                                key={e.categoryId}
                                onClick={() => handleSelection(index)}
                                className={`tw-flex tw-items-center tw-gap-4 tw-p-3.5 tw-text-sm tw-cursor-pointer ${e.selected ? 'tw-bg-accent-tint' : 'tw-bg-surface'} tw-border tw-border-t-0 tw-border-edge`}
                            >
                                <span onClick={(ev) => ev.stopPropagation()} className="tw-flex tw-items-center">
                                    <Checkbox
                                        checked={!!e.selected}
                                        onChange={() => handleSelection(index)}
                                        aria-label={e.categoryName ?? 'Category'}
                                        data-testid={`promotion-dialog-row-checkbox-${index}`}
                                    />
                                </span>
                                <div className="tw-w-[60%] tw-min-w-0">
                                    <Typography variant="body-primary" as="div" className="tw-truncate">{e.categoryName ?? ''}</Typography>
                                </div>
                                <div className="tw-flex tw-w-[30%]">
                                    <Tooltip
                                        usePortal
                                        placement="right"
                                        content={
                                            <div className="tw-px-2 tw-py-1.5">
                                                <Typography variant="sub-title-label" as="div" className="tw-text-ink-secondary">Model</Typography>
                                                {e.models.map((m: any) => (
                                                    <Typography key={m.modelId} variant="body-primary" as="div" className="tw-mt-2">{m.modelName}</Typography>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <span className="tw-px-1 tw-font-medium tw-text-center tw-text-accent-bold tw-rounded-ctl hover:tw-bg-surface-hover-strong tw-min-w-[22px] tw-inline-block">
                                            {e.models.length ?? 0}
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>;
                        })}
                    </div>
                </>
            )}
        </Dialog>
    );
}

export default PromotionDialog;
